import type Board from '@highcharts/dashboards/es-modules/Dashboards/Board';
import type { TranslateService } from '@ngx-translate/core';
import { isRecord } from '@h20/contracts/utils/object';
import { translated } from '@h20-shared/utils/translate-utils';
import { editModeOf } from './dashboard-settings-panel';
import type { DashboardEditMode, DashboardEditModeStrings } from './dashboard.types';
import { authoringSidebar } from './widgets/dashboard-widget-sidebar';

/** Flat `editMode.lang` key -> platform i18n key. */
const LANG_KEYS = {
    addComponent: 'Dashboard.EditMode.AddComponent',
    cancelButton: 'Dashboard.EditMode.Cancel',
    confirmButton: 'Dashboard.EditMode.Confirm',
    confirmDestroyCell: 'Dashboard.EditMode.ConfirmDestroyCell',
    confirmDestroyRow: 'Dashboard.EditMode.ConfirmDestroyRow',
    confirmDiscardChanges: 'Dashboard.EditMode.ConfirmDiscardChanges',
    editMode: 'Dashboard.EditMode.EditMode',
    errorMessage: 'Dashboard.EditMode.ErrorMessage',
    exitFullscreen: 'Dashboard.EditMode.ExitFullscreen',
    settings: 'Dashboard.EditMode.Settings',
    viewFullscreen: 'Dashboard.EditMode.ViewFullscreen',
} as const satisfies Readonly<Record<FlatLangKey, string>>;

type FlatLangKey = keyof Omit<DashboardEditModeStrings, 'accessibility' | 'sidebar'>;

/** `editMode.lang.accessibility.contextMenu` labels. */
const A11Y_CONTEXT_MENU_KEYS = { button: 'Dashboard.EditMode.A11y.ContextMenuButton' } as const;

/** `editMode.lang.accessibility.editMode` labels. */
const A11Y_EDIT_MODE_KEYS = { editMode: 'Dashboard.EditMode.A11y.EditModeToggle' } as const;

/**
 * Translates a `DashboardEditMode` into Dashboards' `editMode` options.
 */
export function buildEditModeOptions(mode: DashboardEditMode, translate: TranslateService): object {
    if (mode === 'none') {
        return { enabled: false };
    }

    const overrides = mode.lang;
    const sidebar = authoringSidebar();
    const sidebarLabels: Record<string, string> = {
        ...resolveAll(translate, sidebar.labelKeys),
        ...sidebar.labels,
        ...overrides?.sidebar,
    };
    // Defaulting rather than merging: a board that names its own entries gets exactly those, in its own order.
    let components = mode.sidebarComponents;
    if (components === undefined) {
        // Sort by the text the author reads, not the entry key, so `widget:grid` sorts under "Table".
        components = [...sidebar.components].sort((first, second) =>
            (sidebarLabels[first] ?? first).localeCompare(
                sidebarLabels[second] ?? second,
                translate.currentLang
            )
        );
    }
    return {
        enabled: true,
        contextMenu: { enabled: false },
        settings: { enabled: mode.permissionLevel !== 'user' },
        toolbars: { sidebar: { components: [...components] } },
        lang: {
            ...resolveAll(translate, LANG_KEYS),
            ...overrides,
            sidebar: sidebarLabels,
            accessibility: {
                contextMenu: {
                    ...resolveAll(translate, A11Y_CONTEXT_MENU_KEYS),
                    ...overrides?.accessibility?.contextMenu,
                },
                editMode: {
                    ...resolveAll(translate, A11Y_EDIT_MODE_KEYS),
                    ...overrides?.accessibility?.editMode,
                },
            },
        },
    };
}

export function activateEditMode(board: Board): void {
    editModeOf(board)?.toggleEditMode();
}

/** Opens the "Add component" sidebar */
export function openAddComponentSidebar(board: Board): boolean {
    const editMode = editModeOf(board);
    if (editMode === undefined) {
        return false;
    }
    const sidebar: unknown = editMode.sidebar;
    if (!isRecord(sidebar) || typeof sidebar['show'] !== 'function') {
        return false;
    }
    sidebar['show']();
    if ('setEditOverlay' in editMode && typeof editMode.setEditOverlay === 'function') {
        editMode.setEditOverlay();
    }
    return true;
}

function resolveAll(
    translate: TranslateService,
    keys: Readonly<Record<string, string>>
): Record<string, string> {
    const resolved: Record<string, string> = {};
    for (const [langKey, i18nKey] of Object.entries(keys)) {
        const text = translated(translate, i18nKey);
        if (text !== i18nKey) {
            resolved[langKey] = text;
        }
    }
    return resolved;
}
